/**
 * Simple referral tracking helper.
 * Captures ?ref= parameter and stores it in localStorage for testing.
 */

import { get, throwHttpError } from './api';
import { getFromLocalStorage, isValidString, setLocalStorage } from '../utils';

import { getQueryStringParameters } from '../urls';
import { inIframe } from '../device';

const REF_KEY = 'proxi_referral';

/**
 * Stored referral data
 */
interface StoredReferralData {
    refCode: string;
    capturedAt: string;
    url: string;
}

/**
 * Response from getting/creating a referral code
 */
interface ReferralCodeResponse {
    referral_code: string;
}

/**
 * Captures referral code from URL and stores in localStorage.
 * Also records the visit to the backend if the code is valid.
 */
export async function storeReferralFromUrl(url: string): Promise<void> {
    try {
        const queryString = getQueryStringParameters();
        const refCode = queryString.get('ref') ?? queryString.get('referral') ?? '';

        if (!refCode) {
            return;
        }

        // get origin of parent of iframe. we don't want to save if the overarching parent page is proxi.co since that is our own domain
        const parent_url = inIframe() ? document.referrer : window.location.hostname.toLowerCase();
        if (isValidString(parent_url) && parent_url.includes('proxi.co') && !parent_url.includes('map.proxi.co')) {
            return;
        }

        // Store locally
        const storedData: StoredReferralData = {
            refCode,
            capturedAt: new Date().toISOString(),
            url
        };
        setLocalStorage(REF_KEY, JSON.stringify(storedData));
    } catch {
        // Silently fail
    }
}

/**
 * Gets the stored referral code from localStorage.
 * this is used to pre-fill referral code fields during signup/payment
 */
export function getStoredReferralCode(): string | undefined {
    const dataStr = getFromLocalStorage(REF_KEY);
    if (!dataStr) return undefined;
    
    try {
        const data: StoredReferralData = JSON.parse(dataStr);
        return data.refCode;
    } catch {
        return undefined;
    }
}


/**
 * Get or create a referral code for an organization.
 * This endpoint requires write access to the organization.
 * @param organizationId The organization ID
 * @param organizationKey Optional organization key for auth
 * @returns The referral code string
 */
export async function getOrCreateOrganizationReferralCode(
    organizationId: string,
    organizationKey?: string
): Promise<string> {
    let url = `/api/organizations/${organizationId}/referral-code`;
    if (organizationKey) {
        url += `?organization_key=${encodeURIComponent(organizationKey)}`;
    }
    
    const response = await fetch(url, get());
    if (!response.ok) {
        await throwHttpError('Failed to get/create referral code', response);
    }
    
    const data: ReferralCodeResponse = await response.json();
    return data.referral_code;
}

/**
 * Get or create a referral code for an activated challenge.
 */
export async function getOrCreateActivatedChallengeReferralCode(
    activatedChallengeId: string,
    activatedChallengeKey: string
): Promise<string> {
    let url = `/api/activated_challenges/${activatedChallengeId}/referral-code`;
    if (activatedChallengeKey) {
        url += `?activated_challenge_key=${encodeURIComponent(activatedChallengeKey)}`;
    }

    const response = await fetch(url, get());
    if (!response.ok) {
        await throwHttpError('Failed to get/create referral code', response);
    }

    const data: ReferralCodeResponse = await response.json();
    return data.referral_code;
}

/**
 * Get or create a referral code for an account.
 * This endpoint requires authentication.
 * @returns The referral code string
 */
export async function getOrCreateAccountReferralCode(): Promise<string> {
    const response = await fetch('/api/accounts/referral-code', get());
    if (!response.ok) {
        await throwHttpError('Failed to get/create account referral code', response);
    }

    const data: ReferralCodeResponse = await response.json();
    return data.referral_code;
}
